import { memo, useCallback } from 'react';
import { LatLng, LatLngBounds, LatLngExpression, Map as LeafletMap } from 'leaflet';
import { MdMyLocation } from 'react-icons/md';
import { FaPlus, FaMinus } from 'react-icons/fa6';
import { IconButton } from '@ds';
import { TRACKING_FIT_BOUNDS_OPTIONS } from '@features/components/LocationTracking/utils/bounds.utils';

type MapControlsProps = {
  map: LeafletMap;
  defaultCenter: LatLngExpression;
  anchor?: LatLng | null;
  resetBounds?: LatLngBounds | null;
};

export const MapControls = memo(({ map, defaultCenter, anchor, resetBounds }: MapControlsProps) => {
  const zoomTo = useCallback(
    (zoom: number) => {
      const target = anchor ?? map.getCenter();
      if (map.getBounds().contains(target)) {
        map.setZoomAround(target, zoom);
        return;
      }
      map.setView(target, zoom);
    },
    [map, anchor],
  );

  const handleReset = useCallback(() => {
    if (resetBounds?.isValid()) {
      map.flyToBounds(resetBounds, TRACKING_FIT_BOUNDS_OPTIONS);
      return;
    }
    map.flyTo(defaultCenter);
  }, [map, resetBounds, defaultCenter]);

  const handleZoomIn = useCallback(() => zoomTo(map.getZoom() + 1), [map, zoomTo]);
  const handleZoomOut = useCallback(() => zoomTo(map.getZoom() - 1), [map, zoomTo]);

  return (
    <div className="absolute z-402 right-4 bottom-4 flex flex-col gap-2" role="group" aria-label="Map controls">
      <IconButton
        size="sm"
        type="button"
        radius="sm"
        aria-label="Reset map position"
        className="text-base-5 bg-white data-[hover=true]:bg-base-80"
        startContent={<MdMyLocation size={24} />}
        onPress={handleReset}
      />
      <IconButton
        size="sm"
        type="button"
        radius="sm"
        aria-label="Zoom in"
        className="text-base-5 bg-white data-[hover=true]:bg-base-80"
        startContent={<FaPlus size={24} />}
        onPress={handleZoomIn}
      />
      <IconButton
        size="sm"
        type="button"
        radius="sm"
        aria-label="Zoom out"
        className="text-base-5 bg-white data-[hover=true]:bg-base-80"
        startContent={<FaMinus size={24} />}
        onPress={handleZoomOut}
      />
    </div>
  );
});
